import $ from 'jquery'
import { CastingNewOrEditProps } from '../json-schemas/serializers/casting-new-or-edit-props'
import { websiteCastingWidgetPluginOfferedService } from '../models/website-casting-widgets.service'
import { geocodingGeodesicDistance } from '../utils/geocoding.service'
import { moment } from '../utils/moment.service'
import { formatTime, parseTime, parseTimeRange } from '../utils/utils.service'
import { CastingStore } from './casting.store'
import { OfferedServiceBase } from '../json-schemas/base/offered_service'

const i18nDefault = JSON.stringify(window.i18n)

/** reads window.casting.slots and virtually inject removed fields like service_duration, etc. */
function compatibilityParseSlots (casting: CastingNewOrEditProps['casting']) {
  if (!casting.slots || !casting.slots.length) return

  // Slots that requires multiple employees (with slot.employees_count) stores the total duration "duration_per_employee" * "employees_count".
  // For the UI, it is more convenient to store the "duration_per_employee" as slot.duration.
  for (const slot of casting.slots) if (slot.employees_count) slot.duration /= slot.employees_count

  // basic support for recurring slot preceded by a oneshot slot of another duration
  if (casting.slots.some(_ => _.weeks_interval) && casting.slots.some(_ => !_.weeks_interval)) {
    const oneshotSlot = casting.slots.find(_ => !_.weeks_interval)
    casting.slots = casting.slots.filter(_ => _ != oneshotSlot)

    for (const slot of casting.slots) {
      if (moment(slot.start_date).subtract(slot.weeks_interval, 'weeks').isSame(moment(oneshotSlot.start_date))) {
        slot.start_date = oneshotSlot.start_date
      }
    }
  }

  // run some assertion before trandforming data
  if (casting.slots.find(_ => _.weeks_interval !== casting.slots[0].weeks_interval)) throw new Error(`Not yet supported: casting #${casting.id} mixes various weeks intervals.`)

  const optionDuration = (casting.selected_options || [])
    .filter(_ => _.offered_option.option_type === 'given_duration')
    .reduce((acc, curr) => acc + curr.selected_value, 0)

  // remove option duration from each slot (option duration is included in the slot for the backend, but excluded from the front-end perspective)
  if (optionDuration) {
    for (const slot of casting.slots) {
      for (const allowedPattern of slot.allowed_patterns) {
        const parsedRange = parseTimeRange(allowedPattern.from_hour, allowedPattern.until_hour)
        const patternDuration = (parsedRange[1] - parsedRange[0]) * 3600

        if (slot.duration === patternDuration) {
          allowedPattern.until_hour = formatTime((parseTime(allowedPattern.until_hour) - (optionDuration / 3600) + 24) % 24)
        }
      }

      slot.duration -= optionDuration
    }
  }
}

export async function initEditCasting (store: CastingStore, { casting, market, params }: CastingNewOrEditProps) {
  if (!casting) return

  compatibilityParseSlots(casting)
  if (casting.city) await store.loadSerializedCity(casting)

  if (casting.offered_service_id) {
    const offeredService = await websiteCastingWidgetPluginOfferedService(market, casting.offered_service_id, {
      token: store.data.invitationToken,
      customer_id: store.data.customer?.id
    })

    await store.setOfferedService(offeredService, { runCallbacks: true, runResets: false })
  }

  store.data.editedCastingId = casting.id || null
  store.data.serviceCategory = params?.service_category_id ? market.service_categories.find(_ => _.id === params.service_category_id) : null
  store.data.requiredSupplier = casting.required_supplier ?? null
  store.data.requiredEmployees = casting.required_employees || []
  store.data.slots = casting.slots || []
  store.data.castingType = !casting.slots?.length ? null : (casting.slots[0].weeks_interval ? 'casting_recurring' : 'casting_one_shot')
  store.data.attachments = params?.init_attachments?.map(_ => ({ ..._, state: 'success' })) || []
  store.data.quantity = casting.quantity ?? null
  store.data.additionalData = casting.additional_data ?? null
  store.data.selectedItemPreferenceIds = casting.item_preference_ids || []
  store.data.selectedTaskItemIds = casting.item_task_ids || []
  store.data.pets = casting.presence_of_pets || []
  store.data.variableMetier = casting.variable_metier ?? null
  store.data.selectedDurationCalculatorItems = casting.selected_duration_calculator_items || []
  store.data.message = casting.message ?? null
  store.data.selectedCombinationAttrs = casting.selected_combination_attrs || []
  store.data.participantsNumber = casting.participants_number || 0
  store.data.urssafPayment = casting.urssaf_payment || false
  store.data.appointmentRequiredRanges = casting.appointment_required_ranges
  store.data.backofficeSkipsWaitingOrderHoursPeriod = !!casting.id

  if (store.data.offeredService) {
    if (casting.id) store.data.offeredService.asap_only = false
    store.data.serviceCategory ||= market.service_categories.find(_ => _.offered_service_ids.includes(store.data.offeredService.id)) || null
    store.data.requiredPlace = casting.required_place || store.data.offeredService.intervention_places[0]
    store.data.coupon = casting.bought_coupons_pack ? store.data.offeredService.coupons_packs.find(_ => _.id === casting.bought_coupons_pack.coupons_pack_id) : null

    if (store.data.city && store.data.requiredPlace === 'on_site') {
      let sdl = store.data.offeredService.service_delivery_locations.find(e => e.id == casting.service_delivery_location_id) || null

      if (!sdl && store.data.offeredService.service_delivery_locations.length === 1 || !casting.service_delivery_location_id) {
        sdl = store.data.offeredService.service_delivery_locations[0]
      }
      
      if (sdl) store.data.serviceDeliveryLocation = { ...sdl, distance: geocodingGeodesicDistance(store.data.city, sdl) }
    }

    store.data.selectedOptions = (casting.selected_options || [])
      .filter(so => store.data.offeredService.offered_options.find(oo => oo.id === so.offered_option_id))
      .map(so => ({ offered_option_id: so.offered_option_id, selected_value: so.selected_value, offered_service_id: null as OfferedServiceBase['id'] }))
    
    // ensure the offered service is consistent with current user identity and run callbacks (ex. i18n population)
    await store.setOfferedService({ ...store.data.offeredService }, { runCallbacks: true, runResets: false })

    if (store.data.slots.length && window.currentUser?.type === 'Operator' && store.isComplex()) {
      store.data.complexVariantMonoSlotMultiAllowedPatterns = store.data.slots.length === 1 && store.data.slots[0].allowed_patterns.length > 1
    }
  }
    
  // try to go at second step if requested in params
  if ((params?.step === 2 || casting.id) && !(window as any).mobile) {
    const intervalId = setInterval(() => {
      if (!$('.js-test-casting-step-submit').prop('disabled')) {
        $('.js-test-casting-step-submit').click()
        clearInterval(intervalId)
      }
    }, 100)
  }
}

/** Return the next gen store, used to communicate with casting widget components */
export function getNextGenStore ({ market, prescriber, referrerCustomer, invitation, customer, countries }: CastingNewOrEditProps) {
  const store = new CastingStore()
  store.init({ renderingMode: (window as any).mobile ? 'mobile' : 'desktop', market, serviceCategories: market.service_categories, countries })

  store.set({ prescriber, invitation, referrerCustomer, customer }, { runCallbacks: false, runResets: false })

  store.changeListeners.push((data, oldData, changes) => {
    if (changes.includes('offeredService') && data.offeredService) {
      // replace translations based on service
      window.i18n = { ...window.i18n, ...JSON.parse(i18nDefault) }
      if (data.offeredService.frontoffice_translations?.length > 0) replaceTranslations(data.offeredService)
    }
  })

  return store
}

function replaceTranslations (offeredService: CastingStore['data']['offeredService']) {
  const newTranslations = offeredService.frontoffice_translations.filter(e => e.locale.split('-')[0] == window.locale.split('-')[0])

  for (const t of newTranslations) {
    const splitedKey = t.key.split('.').slice(1)
    let subObject = window.i18n
    for (let i = 0; i < splitedKey.length - 1; i++) subObject = subObject[splitedKey[i]] || {}
    subObject[splitedKey[splitedKey.length - 1]] = t.value
  }
}
